import { STORY_STATUS } from "@/lib/db/schema";
import type { StoryGenre, StoryStatus } from "@/lib/actions/story-actions";
import { GENRE_REGISTRY, LEGACY_GENRE_LABELS } from "@/lib/genres/registry";

// ─── Genre / Status Constants ────────────────────────────
// Genre identity (enum value, label, slug, icon) lives in
// `lib/genres/registry.ts` — the single source of truth. The exports below
// are DERIVED from it, so adding a genre never means editing this file.
//
// The DB enum (`story_genre`) still contains the original 4 values
// (fantasy, sci_fi, romance, action) for legacy data; they are not in the
// registry and so never appear in the filter UI. Stories tagged with a
// legacy value won't surface on any /genre/* page until re-tagged, but their
// badge still renders via LEGACY_GENRE_LABELS (see ALL_GENRE_LABELS).

export const GENRES = GENRE_REGISTRY.map((g) => ({
    value: g.enumValue,
    label: g.label,
    icon: g.icon,
}));

export const STATUSES: { value: StoryStatus; label: string }[] = [
    { value: STORY_STATUS.ONGOING, label: "Ongoing" },
    { value: STORY_STATUS.COMPLETED, label: "Completed" },
    { value: STORY_STATUS.HIATUS, label: "Hiatus" },
];

/**
 * Display labels for *every* genre enum value, including the legacy four
 * (fantasy, sci_fi, romance, action) that aren't surfaced in the filter UI
 * but may still be attached to stories. Used to render genre badges on the
 * reader discovery page. Derived: registry labels + the static legacy map.
 */
export const ALL_GENRE_LABELS: Record<StoryGenre, string> = {
    ...(LEGACY_GENRE_LABELS as Record<StoryGenre, string>),
    ...Object.fromEntries(GENRE_REGISTRY.map((g) => [g.enumValue, g.label])),
} as Record<StoryGenre, string>;

/** Maps a raw genre enum value to a human label; returns null if unknown. */
export function genreLabel(genre: string | null | undefined): string | null {
    if (!genre) return null;
    return ALL_GENRE_LABELS[genre as StoryGenre] ?? null;
}

/** Set of valid genre values – useful for URL param validation. */
export const VALID_GENRES = new Set<StoryGenre>(
    GENRE_REGISTRY.map((g) => g.enumValue),
);

/** Set of valid status values – useful for URL param validation. */
export const VALID_STATUSES = new Set<StoryStatus>(STATUSES.map((s) => s.value));
